import React, { useState, useEffect } from 'react';
import {
    Mail,
    MailCheck,
    MailX,
    TrendingUp,
    RefreshCw,
    AlertCircle,
    Clock,
    BarChart3,
    Send
} from 'lucide-react';
import { apiClient, EmailAnalyticsResponse } from '../services/api';

const LOG_PAGE_SIZE = 15;

interface EmailAnalyticsProps {
    userRole: string;
}

const EmailAnalytics: React.FC<EmailAnalyticsProps> = ({ userRole }) => {
    const [analytics, setAnalytics] = useState<EmailAnalyticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
    const [logPage, setLogPage] = useState(1);

    useEffect(() => {
        loadEmailAnalytics();
        // Auto-refresh every 60 seconds (less frequent than system health)
        const interval = setInterval(loadEmailAnalytics, 60000);
        return () => clearInterval(interval);
    }, []);

    const loadEmailAnalytics = async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            const response = await apiClient.getEmailAnalytics();
            setAnalytics(response);
            setLastRefresh(new Date());
            setLogPage(1);
        } catch (err: any) {
            setError(err.message || 'Failed to load email analytics');
            console.error('Email analytics error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBar = (status: string): string => {
        switch (status) {
            case 'delivered':            return 'bg-green-500';
            case 'sent':                 return 'bg-blue-500';
            case 'pending':              return 'bg-amber-400';
            case 'bounced':
            case 'dropped':
            case 'spam':
            case 'failed':               return 'bg-red-500';
            case 'deferred':             return 'bg-yellow-400';
            default:                     return 'bg-gray-300';
        }
    };

    const getStatusLabel = (status: string): string =>
        status === 'sent' ? 'delivered' : status;

    const getEmailTypeLabel = (type: string) => {
        switch (type) {
            case 'consent_request':
            case 'Consent Request':
                return 'Consent Request';
            case 'consent_confirmation':
            case 'Consent Confirmed':
                return 'Consent Confirmed';
            case 'consent_declined':
            case 'Consent Declined':
                return 'Consent Declined';
            case 'consent_declined_confirmation':
                return 'Consent Declined Confirmation';
            case 'consent_withdrawn':
            case 'Consent Withdrawn':
            case 'driver_consent_withdrawn':
                return 'Consent Withdrawn';
            case 'consent_reminder':
                return 'Consent Reminder';
            case 'consent_expiry':
                return 'Consent Expiry';
            case 'consent_provided':
                return 'Consent Provided';
            case 'expiry_warning':
            case 'licence_expiry':
                return 'Licence Expiry';
            case 'daily_digest':
                return 'Daily Digest';
            case 'bulk_operation':
                return 'Bulk Operation';
            case 'compliance_alert':
                return 'Compliance Alert';
            case 'password_reset':
                return 'Password Reset';
            case 'report_ready':
                return 'Report Ready';
            case 'admin_notification':
                return 'Admin Notification';
            default:
                return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (isLoading && !analytics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#69BE28' }} />
                    <span className="text-gray-600">Loading email analytics...</span>
                </div>
            </div>
        );
    }

    if (error && !analytics) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Email Analytics</h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={loadEmailAnalytics}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* Refresh row */}
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                    Last updated: {formatTime(lastRefresh.toISOString())}
                </span>
                <button
                    type="button"
                    onClick={loadEmailAnalytics}
                    disabled={isLoading}
                    className="inline-flex min-h-11 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#4a8a1c] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-50"
                    aria-label="Refresh email analytics"
                    aria-busy={isLoading}
                >
                    <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {analytics && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Total Emails */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                            <div className="flex items-center">
                                <div className="p-2 rounded-lg mr-4" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                                    <Mail className="w-6 h-6" style={{ color: '#69BE28' }} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Total Emails</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.totalEmails}</p>
                                </div>
                            </div>
                        </div>

                        {/* Delivery Rate */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg mr-4">
                                    <TrendingUp className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
                                    <p className="text-2xl font-bold text-green-600">
                                        {analytics.data.summary.deliveryRate.toFixed(1)}%
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Successful Deliveries */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg mr-4">
                                    <MailCheck className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Delivered</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.deliveredEmails}</p>
                                </div>
                            </div>
                        </div>

                        {/* Failed Deliveries */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-red-100 rounded-lg mr-4">
                                    <MailX className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-600">Failed</p>
                                    <p className="text-2xl font-bold text-gray-900">{analytics.data.summary.failedEmails}</p>
                                    <p className="text-xs text-gray-500">
                                        Avg {analytics.data.summary.avgRetryCount.toFixed(1)} retries
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Status Breakdown */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <BarChart3 className="w-5 h-5 mr-2" />
                                Delivery Status
                            </h3>
                            <div className="space-y-3">
                                {Object.entries(analytics.data.statusBreakdown)
                                    .sort(([, a], [, b]) => (b as number) - (a as number))
                                    .map(([status, count]) => {
                                        const pct = analytics.data.summary.totalEmails > 0
                                            ? Math.round(((count as number) / analytics.data.summary.totalEmails) * 100)
                                            : 0;
                                        return (
                                            <div key={status}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 capitalize">
                                                        <span className={`h-4 w-[3px] rounded-full ${getStatusBar(status)}`} aria-hidden="true" />
                                                        {status}
                                                    </span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                                                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div
                                                        className="h-1.5 rounded-full transition-all duration-300"
                                                        style={{ backgroundColor: '#69BE28', width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>

                        {/* Email Type Breakdown */}
                        <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6 lg:col-span-2">
                            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                                <Send className="w-5 h-5 mr-2" />
                                Email Type Breakdown
                            </h3>
                            <div className="space-y-3 overflow-y-auto max-h-64 pr-1">
                                {Object.entries(analytics.data.typeBreakdown)
                                    .sort(([, a], [, b]) => (b as number) - (a as number))
                                    .map(([type, count]) => {
                                        const pct = analytics.data.summary.totalEmails > 0
                                            ? Math.round(((count as number) / analytics.data.summary.totalEmails) * 100)
                                            : 0;
                                        return (
                                            <div key={type}>
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className="text-sm text-gray-700">{getEmailTypeLabel(type)}</span>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="text-sm font-semibold text-gray-900">{count as number}</span>
                                                        <span className="text-xs text-gray-400 w-8 text-right">{pct}%</span>
                                                    </div>
                                                </div>
                                                <div className="w-full bg-gray-100 rounded-full h-1.5">
                                                    <div
                                                        className="h-1.5 rounded-full transition-all duration-300"
                                                        style={{ backgroundColor: '#69BE28', width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>

                    {/* Recent Email Activity */}
                    {(() => {
                        const logs = analytics.data.recentLogs;
                        const totalLogPages = Math.ceil(logs.length / LOG_PAGE_SIZE);
                        const paginatedLogs = logs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE);
                        const from = logs.length === 0 ? 0 : (logPage - 1) * LOG_PAGE_SIZE + 1;
                        const to = Math.min(logPage * LOG_PAGE_SIZE, logs.length);

                        return (
                            <div className="bg-white rounded-xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]">
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                        <Clock className="w-5 h-5 mr-2" />
                                        Recent Email Activity
                                    </h3>
                                    {logs.length > 0 && (
                                        <span className="text-sm text-gray-500">
                                            {from}–{to} of {logs.length}
                                        </span>
                                    )}
                                </div>

                                {logs.length === 0 ? (
                                    <div className="text-center py-8">
                                        <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500">No recent email activity</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-100">
                                                <thead className="sticky top-0 z-10 border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                                    <tr>
                                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Recipient</th>
                                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Retries</th>
                                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Sent Date</th>
                                                        <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {paginatedLogs.map((log) => (
                                                        <tr key={log.id} className="hover:bg-gray-50/60 transition-colors">
                                                            <td className="px-4 py-3.5 text-sm">
                                                                <div className="text-gray-900 font-medium">{log.recipientEmail}</div>
                                                                {log.bounceReason && (
                                                                    <div className="text-xs text-red-600 mt-1">{log.bounceReason}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3.5 text-sm text-gray-700">{getEmailTypeLabel(log.emailType)}</td>
                                                            <td className="px-4 py-3.5 text-sm">
                                                                {log.retryCount > 0
                                                                    ? <span className="text-yellow-600 font-medium">{log.retryCount}</span>
                                                                    : <span className="text-gray-400">0</span>}
                                                            </td>
                                                            <td className="px-4 py-3.5 text-sm text-gray-500">
                                                                <div>{formatDate(log.sentAt)}</div>
                                                                <div className="text-xs">{formatTime(log.sentAt)}</div>
                                                            </td>
                                                            <td className="px-4 py-3.5">
                                                                <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700 capitalize">
                                                                    <span className={`h-4 w-[3px] rounded-full ${getStatusBar(log.deliveryStatus)}`} aria-hidden="true" />
                                                                    {getStatusLabel(log.deliveryStatus)}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {totalLogPages > 1 && (
                                            <div className="mt-6 flex justify-between items-center px-6 pb-4">
                                                <div className="text-sm text-gray-600">
                                                    Page {logPage} of {totalLogPages}
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => setLogPage(p => Math.max(1, p - 1))}
                                                        disabled={logPage === 1}
                                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Previous
                                                    </button>
                                                    <button
                                                        onClick={() => setLogPage(p => Math.min(totalLogPages, p + 1))}
                                                        disabled={logPage === totalLogPages}
                                                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Next
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })()}
                </>
            )}
        </div>
    );
};

export default EmailAnalytics;